import { Injectable } from '@angular/core';
import type { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { environment } from '../../environments/environment';

type ApplicationInsightsConstructor = new (config: {
  config: {
    connectionString: string;
    enableAutoRouteTracking: boolean;
    enableCorsCorrelation: boolean;
    enableRequestHeaderTracking: boolean;
    enableResponseHeaderTracking: boolean;
    enableAjaxErrorStatusText: boolean;
  };
}) => ApplicationInsights;

@Injectable({
  providedIn: 'root',
})
export class ApplicationInsightsService {
  private appInsights: ApplicationInsights | null = null;

  constructor() {
    this.initialize();
  }

  public initialize(): void {
    void this.initializeApplicationInsights();
  }

  private async initializeApplicationInsights(): Promise<void> {
    if (this.appInsights) {
      return;
    }

    if (
      !environment.applicationInsights?.applicationInsightsConfig
        ?.connectionString
    ) {
      if (!this.isTestEnvironment()) {
        console.warn(
          'Application Insights connection string is not configured',
        );
      }
      return;
    }

    try {
      const testSdk = this.getTestSdkConstructor();
      if (testSdk) {
        this.createAppInsightsInstance(testSdk);
        return;
      }

      const { ApplicationInsights: ApplicationInsightsSdk } =
        await import('@microsoft/applicationinsights-web');
      this.createAppInsightsInstance(
        ApplicationInsightsSdk as ApplicationInsightsConstructor,
      );
    } catch (error) {
      console.error('Failed to initialize Application Insights:', error);
    }
  }

  private getTestSdkConstructor(): ApplicationInsightsConstructor | null {
    if (!this.isTestEnvironment()) {
      return null;
    }

    const globalRef = globalThis as unknown as {
      __APP_INSIGHTS_SDK__?: ApplicationInsightsConstructor;
    };
    return globalRef.__APP_INSIGHTS_SDK__ ?? null;
  }

  private isTestEnvironment(): boolean {
    const processRef = globalThis as unknown as {
      process?: {
        env?: {
          NODE_ENV?: string;
          JEST_WORKER_ID?: string;
        };
      };
    };

    return (
      processRef.process?.env?.NODE_ENV === 'test' ||
      processRef.process?.env?.JEST_WORKER_ID !== undefined
    );
  }

  private createAppInsightsInstance(
    ApplicationInsightsSdk: ApplicationInsightsConstructor,
  ): void {
    this.appInsights = new ApplicationInsightsSdk({
      config: {
        connectionString:
          environment.applicationInsights.applicationInsightsConfig
            .connectionString,
        enableAutoRouteTracking: true,
        enableCorsCorrelation: true,
        enableRequestHeaderTracking: true,
        enableResponseHeaderTracking: true,
        enableAjaxErrorStatusText: true,
      },
    });

    this.appInsights.loadAppInsights();

    this.appInsights.addTelemetryInitializer((envelope) => {
      if (envelope.tags) {
        envelope.tags['ai.cloud.role'] = 'StopPortalFrontend';
        envelope.tags['ai.cloud.roleInstance'] = window.location.hostname;
      }
    });

    if (!environment.production) {
      console.log('Application Insights initialized successfully');
    }
  }

  public getAppInsights(): ApplicationInsights | null {
    return this.appInsights;
  }

  public trackEvent(
    name: string,
    properties?: Record<string, string | number | boolean>,
    measurements?: Record<string, number>,
  ): void {
    if (this.appInsights) {
      this.appInsights.trackEvent({ name, properties, measurements });
    }
  }

  public trackException(exception: Error): void {
    if (this.appInsights) {
      this.appInsights.trackException({ exception, severityLevel: 2 });
    }
  }

  public trackPageView(name?: string, url?: string): void {
    if (this.appInsights) {
      this.appInsights.trackPageView({ name, uri: url });
    }
  }
}
